import { createApi } from '@reduxjs/toolkit/query/react';

import baseQuery from 'src/utils/baseQuery';

export type ConversationLearningStatus =
  | 'PENDING_REVIEW'
  | 'NEEDS_REVIEW'
  | 'APPROVED'
  | 'REJECTED';

export interface ConversationLearning {
  id: string;
  tenantId: string;
  agentId: string;
  sessionId: string;
  question: string;
  answer: string;
  status: ConversationLearningStatus;
  source: string;
  reviewedById?: string;
  reviewedAt?: string;
  kbDocId?: string;
  createdAt: string;
  updatedAt: string;
}

export interface KnowledgeGap {
  id: string;
  tenantId: string;
  agentId: string;
  sessionId: string;
  question: string;
  exitReason: string;
  clusterKey?: string;
  clusterCount: number;
  createdAt: string;
}

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
}

export const agentLearningApi = createApi({
  reducerPath: 'agentLearningApi',
  baseQuery,
  tagTypes: ['LearningQueue', 'KnowledgeGaps'],
  endpoints: (builder) => ({
    // ── Learning Queue ──────────────────────────────────────────────────────────
    getLearningQueue: builder.query<
      PaginatedResponse<ConversationLearning>,
      { agentId?: string; status?: ConversationLearningStatus; page?: number; limit?: number }
    >({
      query: (params) => ({
        url: 'api/ai-agents/learning-queue',
        params,
      }),
      transformResponse: (res: any) => res.data ?? res,
      providesTags: ['LearningQueue'],
    }),

    approveLearning: builder.mutation<ConversationLearning, string>({
      query: (id) => ({
        url: `api/ai-agents/learning-queue/${id}/approve`,
        method: 'POST',
      }),
      transformResponse: (res: any) => res.data ?? res,
      invalidatesTags: ['LearningQueue'],
    }),

    rejectLearning: builder.mutation<ConversationLearning, string>({
      query: (id) => ({
        url: `api/ai-agents/learning-queue/${id}/reject`,
        method: 'POST',
      }),
      transformResponse: (res: any) => res.data ?? res,
      invalidatesTags: ['LearningQueue'],
    }),

    // ── Knowledge Gaps ──────────────────────────────────────────────────────────
    getKnowledgeGaps: builder.query<
      PaginatedResponse<KnowledgeGap>,
      { agentId?: string; page?: number; limit?: number }
    >({
      query: (params) => ({
        url: 'api/ai-agents/knowledge-gaps',
        params,
      }),
      transformResponse: (res: any) => res.data ?? res,
      providesTags: ['KnowledgeGaps'],
    }),

    resolveKnowledgeGap: builder.mutation<KnowledgeGap, string>({
      query: (id) => ({
        url: `api/ai-agents/knowledge-gaps/${id}/resolve`,
        method: 'POST',
      }),
      transformResponse: (res: any) => res.data ?? res,
      invalidatesTags: ['KnowledgeGaps'],
    }),

    triggerGapClustering: builder.mutation<void, void>({
      query: () => ({
        url: 'api/ai-agents/trigger-gap-clustering',
        method: 'POST',
      }),
      invalidatesTags: ['KnowledgeGaps'],
    }),

    triggerSessionLearning: builder.mutation<void, { sessionId: string; agentId: string }>({
      query: ({ sessionId, agentId }) => ({
        url: `api/ai-agents/sessions/${sessionId}/trigger-learning`,
        method: 'POST',
        body: { agentId },
      }),
      invalidatesTags: ['LearningQueue'],
    }),
  }),
});

export const {
  useGetLearningQueueQuery,
  useLazyGetLearningQueueQuery,
  useApproveLearningMutation,
  useRejectLearningMutation,
  useGetKnowledgeGapsQuery,
  useLazyGetKnowledgeGapsQuery,
  useResolveKnowledgeGapMutation,
  useTriggerGapClusteringMutation,
  useTriggerSessionLearningMutation,
} = agentLearningApi;
